import { Link } from '@tanstack/react-router'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Input } from '@workspace/ui/components/input'
import { Textarea } from '@workspace/ui/components/textarea'
import { type FormEvent, useCallback, useEffect, useId, useRef, useState } from 'react'
import { CopyLinkRow } from '../../../../components/copy-link-row'
import { useInvestorContext } from '../../../../contexts/investor-guard'
import { copy } from '../../copy/investor-copy'
import {
  useCreateFounderInviteMutation,
  useRemoveFounderInviteMutation,
} from '../../hooks/use-investor-assistant'
import { SendWithTiles } from '../send-with-tiles'

interface InviteWindowProps {
  open: boolean
  onClose: () => void
  /** The startup to prefill: the name the investor searched for. */
  startup: string
  /** An invite already made: the window opens on its link and drafted message (Open invite). */
  existing?: { startup: string; link: string }
}

type View =
  /** `startup` is the name to prefill; it defaults to the window's own. */
  | { kind: 'form'; startup?: string }
  | { kind: 'ready'; startup: string; link: string }
  | { kind: 'duplicate'; startup: string; inviteId: string }
  | { kind: 'confirmed'; startup: string }

/** Ask a founder to join SeedForge: a Startup field, then the link and the drafted message to send. */
export function InviteWindow({ open, onClose, startup, existing }: InviteWindowProps) {
  const [view, setView] = useState<View>(
    existing ? { kind: 'ready', startup: existing.startup, link: existing.link } : { kind: 'form' }
  )
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogTitle>{copy('invite.title')}</DialogTitle>
        <DialogDescription className="sr-only">{copy('invite.lead')}</DialogDescription>
        {/* Keyed on the prefill so reopening from another place starts afresh. */}
        <InviteBody
          key={startup}
          view={view}
          setView={setView}
          startup={startup}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  )
}

function InviteBody({
  view,
  setView,
  startup,
  onClose,
}: {
  view: View
  setView: (view: View) => void
  startup: string
  onClose: () => void
}) {
  const remove = useRemoveFounderInviteMutation()
  if (view.kind === 'form') {
    const name = view.startup ?? startup
    // A startup the investor named is invited at once; the Following window, with no name, asks for one first.
    return name ? (
      <InviteCreate startup={name} onCreated={setView} />
    ) : (
      <InviteForm startup={name} onCreated={setView} />
    )
  }
  if (view.kind === 'ready')
    return (
      <InviteReady
        view={view}
        onDone={() => setView({ kind: 'confirmed', startup: view.startup })}
        onClose={onClose}
      />
    )
  if (view.kind === 'confirmed') return <InviteConfirmed startup={view.startup} onClose={onClose} />
  if (view.kind === 'duplicate') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-foreground">
          {copy('invite.duplicate', { startup: view.startup })}
        </p>
        {remove.isError && (
          <p role="alert" className="text-sm text-muted-foreground">
            {copy('invite.removeError')}
          </p>
        )}
        <CtaButton
          type="button"
          disabled={remove.isPending}
          onClick={() =>
            remove.mutate(
              { path: { inviteId: view.inviteId } },
              { onSuccess: () => setView({ kind: 'form', startup: view.startup }) }
            )
          }
        >
          {copy('invites.reinvite')}
        </CtaButton>
      </div>
    )
  }
  return null
}

type CreatedInvite = {
  invite: { id: string; startupName: string }
  link: string | null
  duplicate: boolean
}

function viewOf({ invite, link, duplicate }: CreatedInvite): View {
  if (duplicate || !link)
    return { kind: 'duplicate', startup: invite.startupName, inviteId: invite.id }
  return { kind: 'ready', startup: invite.startupName, link }
}

/** Creates the invite for a startup the investor named, as the window opens. Retry asks again. */
function InviteCreate({
  startup,
  onCreated,
}: {
  startup: string
  onCreated: (view: View) => void
}) {
  const mutation = useCreateFounderInviteMutation()
  const started = useRef(false)
  const create = useCallback(
    () =>
      mutation.mutate(
        { body: { startupName: startup } },
        { onSuccess: (response) => onCreated(viewOf(response.data)) }
      ),
    [mutation, startup, onCreated]
  )
  useEffect(() => {
    if (started.current) return
    started.current = true
    create()
  }, [create])
  if (!mutation.isError)
    return <p className="text-sm text-muted-foreground">{copy('invite.creating')}</p>
  return (
    <div className="space-y-4">
      <p role="alert" className="text-sm text-muted-foreground">
        {copy('invite.error')}
      </p>
      <CtaButton type="button" onClick={create}>
        {copy('invite.retry')}
      </CtaButton>
    </div>
  )
}

function InviteForm({ startup, onCreated }: { startup: string; onCreated: (view: View) => void }) {
  const [name, setName] = useState(startup)
  const fieldId = useId()
  const mutation = useCreateFounderInviteMutation()
  const trimmed = name.trim()
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!trimmed) return
    mutation.mutate(
      { body: { startupName: trimmed } },
      {
        onSuccess: (response) => {
          const { invite, link, duplicate } = response.data
          if (duplicate || !link) {
            onCreated({ kind: 'duplicate', startup: invite.startupName, inviteId: invite.id })
            return
          }
          onCreated({ kind: 'ready', startup: invite.startupName, link })
        },
      }
    )
  }
  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-muted-foreground">{copy('invite.lead')}</p>
      <div className="space-y-1.5">
        <label htmlFor={fieldId} className="text-sm font-medium text-foreground">
          {copy('invite.field.startup')}
        </label>
        <Input
          id={fieldId}
          value={name}
          maxLength={120}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      {mutation.isError && (
        <p role="alert" className="text-sm text-muted-foreground">
          {copy('invite.error')}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <CtaButton type="submit" disabled={!trimmed || mutation.isPending}>
          {copy('invite.submit')}
        </CtaButton>
      </div>
    </form>
  )
}

function InviteReady({
  view,
  onDone,
  onClose,
}: {
  view: Extract<View, { kind: 'ready' }>
  onDone: () => void
  onClose: () => void
}) {
  const { investor } = useInvestorContext()
  const startupId = useId()
  const messageId = useId()
  const [message, setMessage] = useState(
    `${copy('invite.draft', { startup: view.startup })}\n${view.link}`
  )
  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">{copy('invite.lead')}</p>
      <div className="space-y-1.5">
        <label htmlFor={startupId} className="text-sm font-medium text-foreground">
          {copy('invite.field.startup')}
        </label>
        <Input id={startupId} value={view.startup} readOnly tabIndex={-1} />
      </div>
      <div className="space-y-2">
        <label htmlFor={messageId} className="text-sm font-medium text-foreground">
          {copy('invite.message.label')}
        </label>
        <Textarea
          id={messageId}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          rows={5}
        />
      </div>
      <SendWithTiles text={message} subject={copy('invite.title')} />
      <CopyLinkRow
        url={view.link}
        label={copy('invite.copyLink')}
        copiedLabel={copy('invite.copied')}
      />
      <p className="text-xs text-muted-foreground">{copy('invite.quiet')}</p>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/app/investor/$id/follows"
          params={{ id: investor.id }}
          className="text-sm font-medium text-foreground underline-offset-2 hover:underline"
          onClick={onClose}
        >
          {copy('invite.ready.open')}
        </Link>
        <SecondaryButton type="button" onClick={onDone}>
          {copy('invite.ready.done')}
        </SecondaryButton>
      </div>
    </div>
  )
}

/** The confirmation after the link is sent: when the deck goes up, the startup is followed for free. */
function InviteConfirmed({ startup, onClose }: { startup: string; onClose: () => void }) {
  const { investor } = useInvestorContext()
  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold text-foreground">{copy('invite.ready.title')}</h3>
      <p className="text-sm text-muted-foreground">{copy('invite.ready.body', { startup })}</p>
      <p className="text-sm text-muted-foreground">{copy('invite.ready.where')}</p>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/app/investor/$id/follows"
          params={{ id: investor.id }}
          className="text-sm font-medium text-foreground underline-offset-2 hover:underline"
          onClick={onClose}
        >
          {copy('invite.ready.open')}
        </Link>
        <CtaButton type="button" onClick={onClose}>
          {copy('invite.ready.done')}
        </CtaButton>
      </div>
    </div>
  )
}
